import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Shield,
  Zap,
  Users,
  Trophy,
  ArrowRight,
  Play,
  BookOpen,
  Target,
  Flame,
  CheckCircle,
  AlertTriangle,
  DollarSign,
  Radio,
  Star,
  ChevronDown,
  Droplet,
  Home,
  Phone,
  Map,
  Package
} from 'lucide-react'
import Footer from '../components/Footer'

/**
 * LandingScreen Component
 * Public homepage for unauthenticated users
 */
const LandingScreen = () => {
  const navigate = useNavigate()
  const [animatedStats, setAnimatedStats] = useState({ lessons: 0, paths: 0, images: 0 })
  const [openFaq, setOpenFaq] = useState(null)
  const [readinessChecks, setReadinessChecks] = useState({
    water: false,
    food: false,
    firstAid: false,
    communication: false,
    evacuation: false
  })

  // Animate counters on mount
  useEffect(() => {
    const duration = 2000
    const steps = 60
    const interval = duration / steps

    let step = 0
    const timer = setInterval(() => {
      step++
      const progress = step / steps
      setAnimatedStats({
        lessons: Math.floor(250 * progress),
        paths: Math.floor(5 * progress),
        images: Math.floor(1000 * progress)
      })
      if (step >= steps) clearInterval(timer)
    }, interval)

    return () => clearInterval(timer)
  }, [])

  const readinessScore = Object.values(readinessChecks).filter(Boolean).length * 20

  const testimonials = [
    {
      name: 'Sarah M.',
      role: 'Mother of 3',
      quote: 'SurviveIt made emergency prep fun for my whole family. The kids actually ask to do lessons!',
      rating: 5
    },
    {
      name: 'James K.',
      role: 'Former Military',
      quote: 'Finally, a training platform that takes preparedness seriously. The simulations are incredibly realistic.',
      rating: 5
    },
    {
      name: 'Elena R.',
      role: 'Urban Prepper',
      quote: 'The Grid Down path alone is worth it. I feel so much more confident about power outages now.',
      rating: 5
    }
  ]

  const howItWorks = [
    {
      step: 1,
      icon: Target,
      title: 'Choose Your Path',
      description: 'Select from 5 disaster scenarios based on your concerns and location'
    },
    {
      step: 2,
      icon: BookOpen,
      title: 'Learn & Practice',
      description: 'Complete interactive lessons with AI-generated visuals and quizzes'
    },
    {
      step: 3,
      icon: Trophy,
      title: 'Earn & Share',
      description: 'Unlock badges, compete with family, and track your preparedness growth'
    }
  ]

  const faqs = [
    {
      question: 'Is SurviveIt free to use?',
      answer: 'Yes! SurviveIt offers a free tier with 1 complete disaster path (50 lessons), Emergency Mode, and basic features. Premium unlocks all 5 paths (250+ lessons), AI search, Family Sync, and Offline Mode for $4.99/month or $39.99/year.'
    },
    {
      question: 'Do I need any prior survival knowledge?',
      answer: 'Not at all. Our lessons start from the basics and progressively build your skills. Perfect for beginners and experienced preppers alike.'
    },
    {
      question: 'Can my whole family use one account?',
      answer: 'Each family member should have their own account to track individual progress. Premium users can connect accounts through Family Sync to compete and share achievements.'
    },
    {
      question: 'How realistic are the simulations?',
      answer: 'Our Chaos Mode simulations are designed to test your knowledge under pressure with realistic scenarios. They are educational tools, not actual emergencies.'
    },
    {
      question: 'What is Chaos Mode?',
      answer: 'Chaos Mode sends you random emergency alerts while using the app to test your decision-making skills. Free users get 3 alerts per day, Premium users get unlimited.'
    },
    {
      question: 'Can I cancel my subscription?',
      answer: 'Yes, you can cancel anytime. You\'ll keep Premium access until the end of your billing period. We also offer a 7-day money-back guarantee.'
    }
  ]

  const features = [
    {
      icon: BookOpen,
      title: 'Interactive Learning',
      description: 'Master survival skills through engaging modules and hands-on lessons'
    },
    {
      icon: Target,
      title: 'Realistic Simulations',
      description: 'Practice emergency scenarios in safe, controlled environments'
    },
    {
      icon: Trophy,
      title: 'Gamified Progress',
      description: 'Earn badges, level up, and compete with family members'
    },
    {
      icon: Users,
      title: 'Family Challenges',
      description: 'Connect with loved ones and prepare together as a team'
    }
  ]

  const stats = [
    { number: `${animatedStats.lessons}+`, label: 'Lessons' },
    { number: `${animatedStats.paths}`, label: 'Disaster Paths' },
    { number: '24/7', label: 'Emergency Access' },
    { number: `${animatedStats.images}+`, label: 'AI Images' }
  ]

  return (
    <div className="landing-screen">
      {/* Navigation */}
      <nav className="nav">
        <div className="container">
          <div className="nav-content">
            <div className="logo">
              <Shield className="w-8 h-8 text-orange-500" />
              <span className="font-bold text-xl">SurviveIt</span>
            </div>
            <div className="nav-actions">
              <button
                onClick={() => navigate('/pricing')}
                className="btn btn-ghost"
              >
                Pricing
              </button>
              <button
                onClick={() => navigate('/auth')}
                className="btn btn-ghost"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/onboarding')}
                className="btn btn-primary"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-content"
          >
            <h1 className="hero-title">
              Master the Art of
              <span className="text-gradient"> Survival</span>
            </h1>
            <p className="hero-subtitle">
              Interactive training platform that prepares you and your family for any emergency.
              Learn essential survival skills through gamified lessons and realistic simulations.
            </p>
            <div className="hero-actions">
              <button 
                onClick={() => navigate('/onboarding')}
                className="btn btn-primary btn-lg"
              >
                Start Training <ArrowRight className="w-5 h-5" />
              </button>
              <button 
                onClick={() => navigate('/personality-quiz')}
                className="btn btn-secondary btn-lg"
              >
                <Play className="w-5 h-5" />
                Take Personality Quiz
              </button>
            </div>
            <div className="hero-stats">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="stat"
                >
                  <div className="stat-number">{stat.number}</div>
                  <div className="stat-label">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2 className="section-title">Why Choose SurviveIt?</h2>
            <p className="section-subtitle">
              The most comprehensive survival training platform designed for real-world preparedness
            </p>
          </motion.div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="feature-card"
              >
                <div className="feature-icon">
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">
              Get prepared in three simple steps
            </p>
          </motion.div>
          <div className="steps-grid">
            {howItWorks.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="step-card"
              >
                <div className="step-number">{item.step}</div>
                <div className="step-icon">
                  <item.icon className="w-8 h-8" />
                </div>
                <h3 className="step-title">{item.title}</h3>
                <p className="step-description">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="testimonials">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2 className="section-title">Trusted by Thousands</h2>
            <p className="section-subtitle">
              See what our community says about SurviveIt
            </p>
          </motion.div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="testimonial-card"
              >
                <div className="testimonial-stars">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-current text-yellow-500" />
                  ))}
                </div>
                <p className="testimonial-quote">"{testimonial.quote}"</p>
                <div className="testimonial-author">
                  <div className="testimonial-avatar">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <div className="testimonial-name">{testimonial.name}</div>
                    <div className="testimonial-role">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Readiness Calculator */}
      <section className="readiness">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="readiness-card"
          >
            <div className="readiness-header">
              <h2 className="readiness-title">How Prepared Are You?</h2>
              <p className="readiness-subtitle">Quick 30-second assessment</p>
            </div>
            <div className="readiness-checks">
              {[
                { key: 'water', icon: Droplet, label: '72-hour water supply' },
                { key: 'food', icon: Package, label: 'Emergency food kit' },
                { key: 'firstAid', icon: Shield, label: 'First aid supplies' },
                { key: 'communication', icon: Phone, label: 'Emergency contact plan' },
                { key: 'evacuation', icon: Map, label: 'Evacuation route planned' }
              ].map((item) => (
                <label key={item.key} className="readiness-check">
                  <input
                    type="checkbox"
                    checked={readinessChecks[item.key]}
                    onChange={(e) => setReadinessChecks(prev => ({
                      ...prev,
                      [item.key]: e.target.checked
                    }))}
                  />
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </label>
              ))}
            </div>
            <div className="readiness-result">
              <div className="readiness-score-bar">
                <div
                  className="readiness-score-fill"
                  style={{ width: `${readinessScore}%` }}
                />
              </div>
              <div className="readiness-score-text">
                <span className="readiness-score-number">{readinessScore}%</span>
                <span className="readiness-score-label">
                  {readinessScore === 0 && "Let's get started!"}
                  {readinessScore === 20 && "Good first step!"}
                  {readinessScore === 40 && "Making progress!"}
                  {readinessScore === 60 && "Well on your way!"}
                  {readinessScore === 80 && "Almost there!"}
                  {readinessScore === 100 && "Excellent! Keep learning!"}
                </span>
              </div>
              {readinessScore < 100 && (
                <button
                  onClick={() => navigate('/onboarding')}
                  className="btn btn-primary"
                >
                  Improve Your Score <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Disaster Paths Showcase */}
      <section className="paths">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2 className="section-title">Master Any Disaster Scenario</h2>
            <p className="section-subtitle">
              250+ interactive lessons across 5 critical preparedness paths
            </p>
          </motion.div>
          <div className="paths-grid">
            {[
              { icon: Radio, title: 'Nuclear Attack', lessons: 50, color: '#dc2626', desc: 'Shelter, fallout, decontamination' },
              { icon: AlertTriangle, title: 'Pandemic Response', lessons: 50, color: '#7c3aed', desc: 'Quarantine, medical supplies, sanitation' },
              { icon: Zap, title: 'Grid Down Survival', lessons: 50, color: '#f59e0b', desc: 'Power outage, communication, security' },
              { icon: Users, title: 'Civil Unrest Safety', lessons: 50, color: '#3b82f6', desc: 'Evacuation, self-defense, community' },
              { icon: DollarSign, title: 'Economic Crisis', lessons: 50, color: '#10b981', desc: 'Financial prep, barter, resources' },
            ].map((path, index) => (
              <motion.div
                key={path.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="path-card"
              >
                <div className="path-icon" style={{ backgroundColor: `${path.color}20` }}>
                  <path.icon className="w-6 h-6" style={{ color: path.color }} />
                </div>
                <h3 className="path-title">{path.title}</h3>
                <p className="path-desc">{path.desc}</p>
                <span className="path-lessons">{path.lessons} lessons</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="section-header"
          >
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">
              Everything you need to know about SurviveIt
            </p>
          </motion.div>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="faq-item"
              >
                <button
                  className="faq-question"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <span>{faq.question}</span>
                  <ChevronDown
                    className={`w-5 h-5 transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="faq-answer">
                    {faq.answer}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="cta-content"
          >
            <div className="cta-text">
              <h2 className="cta-title">Ready to Become Unstoppable?</h2>
              <p className="cta-subtitle">
                Join thousands of families who trust SurviveIt to prepare for the unexpected
              </p>
              <div className="cta-features">
                <div className="cta-feature">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Free to start</span>
                </div>
                <div className="cta-feature">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>No downloads required</span>
                </div>
                <div className="cta-feature">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>Family-friendly content</span>
                </div>
              </div>
            </div>
            <div className="cta-actions">
              <button 
                onClick={() => navigate('/onboarding')}
                className="btn btn-primary btn-xl"
              >
                <Flame className="w-6 h-6" />
                Begin Your Journey
              </button>
              <button 
                onClick={() => navigate('/emergency')}
                className="btn btn-ghost"
              >
                Emergency Mode →
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />

      <style jsx>{`
        .landing-screen {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: white;
        }

        .nav {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 50;
          background: rgba(15, 23, 42, 0.9);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .nav-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 1rem 0;
        }

        .logo {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .nav-actions {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .hero {
          padding: 8rem 0 4rem;
          text-align: center;
        }

        .hero-content {
          max-width: 800px;
          margin: 0 auto;
        }

        .hero-title {
          font-size: 3.5rem;
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 1.5rem;
        }

        .text-gradient {
          background: linear-gradient(135deg, #f97316 0%, #dc2626 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hero-subtitle {
          font-size: 1.25rem;
          color: rgba(255, 255, 255, 0.8);
          margin-bottom: 2.5rem;
          line-height: 1.6;
        }

        .hero-actions {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-bottom: 3rem;
          flex-wrap: wrap;
        }

        .hero-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
          gap: 2rem;
          max-width: 600px;
          margin: 0 auto;
        }

        .stat {
          text-align: center;
        }

        .stat-number {
          font-size: 2rem;
          font-weight: 700;
          color: #f97316;
          margin-bottom: 0.25rem;
        }

        .stat-label {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.7);
        }

        .features {
          padding: 6rem 0;
          background: rgba(255, 255, 255, 0.05);
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }

        .section-subtitle {
          font-size: 1.125rem;
          color: rgba(255, 255, 255, 0.8);
          max-width: 600px;
          margin: 0 auto;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
        }

        .feature-card {
          background: rgba(255, 255, 255, 0.05);
          padding: 2rem;
          border-radius: 1rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
          transition: transform 0.2s, background 0.2s;
        }

        .feature-card:hover {
          transform: translateY(-4px);
          background: rgba(255, 255, 255, 0.08);
        }

        .feature-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #f97316 0%, #dc2626 100%);
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
          color: white;
        }

        .feature-title {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.75rem;
        }

        .feature-description {
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.6;
        }

        .paths {
          padding: 6rem 0;
        }

        .paths-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
        }

        .path-card {
          background: rgba(255, 255, 255, 0.05);
          padding: 1.5rem;
          border-radius: 1rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
          text-align: center;
          transition: transform 0.2s, background 0.2s;
        }

        .path-card:hover {
          transform: translateY(-4px);
          background: rgba(255, 255, 255, 0.08);
        }

        .path-icon {
          width: 48px;
          height: 48px;
          border-radius: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
        }

        .path-title {
          font-size: 1.125rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .path-desc {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.7);
          margin-bottom: 0.75rem;
        }

        .path-lessons {
          font-size: 0.75rem;
          color: #f97316;
          font-weight: 600;
        }

        /* How It Works */
        .how-it-works {
          padding: 6rem 0;
        }

        .steps-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
          max-width: 900px;
          margin: 0 auto;
        }

        .step-card {
          text-align: center;
          position: relative;
        }

        .step-number {
          position: absolute;
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          width: 28px;
          height: 28px;
          background: #f97316;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.875rem;
        }

        .step-icon {
          width: 64px;
          height: 64px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 1rem auto;
          color: #f97316;
        }

        .step-title {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .step-description {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.7);
          line-height: 1.5;
        }

        /* Testimonials */
        .testimonials {
          padding: 6rem 0;
          background: rgba(255, 255, 255, 0.05);
        }

        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }

        .testimonial-card {
          background: rgba(255, 255, 255, 0.05);
          padding: 2rem;
          border-radius: 1rem;
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .testimonial-stars {
          display: flex;
          gap: 0.25rem;
          margin-bottom: 1rem;
        }

        .testimonial-quote {
          font-size: 1rem;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.9);
          margin-bottom: 1.5rem;
          font-style: italic;
        }

        .testimonial-author {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .testimonial-avatar {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #f97316 0%, #dc2626 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
        }

        .testimonial-name {
          font-weight: 600;
          font-size: 0.875rem;
        }

        .testimonial-role {
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.6);
        }

        /* Readiness Calculator */
        .readiness {
          padding: 6rem 0;
        }

        .readiness-card {
          background: linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%);
          border: 1px solid rgba(249, 115, 22, 0.3);
          border-radius: 1.5rem;
          padding: 3rem;
          max-width: 600px;
          margin: 0 auto;
        }

        .readiness-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .readiness-title {
          font-size: 1.75rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }

        .readiness-subtitle {
          color: rgba(255, 255, 255, 0.7);
        }

        .readiness-checks {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .readiness-check {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 0.75rem;
          cursor: pointer;
          transition: background 0.2s;
        }

        .readiness-check:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        .readiness-check input {
          width: 20px;
          height: 20px;
          accent-color: #f97316;
        }

        .readiness-result {
          text-align: center;
        }

        .readiness-score-bar {
          height: 12px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 6px;
          overflow: hidden;
          margin-bottom: 1rem;
        }

        .readiness-score-fill {
          height: 100%;
          background: linear-gradient(90deg, #f97316 0%, #22c55e 100%);
          border-radius: 6px;
          transition: width 0.5s ease;
        }

        .readiness-score-text {
          margin-bottom: 1.5rem;
        }

        .readiness-score-number {
          font-size: 2rem;
          font-weight: 700;
          color: #f97316;
          display: block;
        }

        .readiness-score-label {
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.875rem;
        }

        /* FAQ */
        .faq {
          padding: 6rem 0;
          background: rgba(255, 255, 255, 0.05);
        }

        .faq-list {
          max-width: 700px;
          margin: 0 auto;
        }

        .faq-item {
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .faq-question {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 0;
          background: transparent;
          border: none;
          color: white;
          font-size: 1rem;
          font-weight: 500;
          text-align: left;
          cursor: pointer;
        }

        .faq-question:hover {
          color: #f97316;
        }

        .faq-answer {
          padding-bottom: 1.5rem;
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.6;
        }

        .cta {
          padding: 6rem 0;
        }

        .cta-content {
          background: linear-gradient(135deg, #f97316 0%, #dc2626 100%);
          padding: 4rem;
          border-radius: 1.5rem;
          text-align: center;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 3rem;
          align-items: center;
        }

        .cta-title {
          font-size: 2.25rem;
          font-weight: 700;
          margin-bottom: 1rem;
        }

        .cta-subtitle {
          font-size: 1.125rem;
          margin-bottom: 2rem;
          opacity: 0.9;
        }

        .cta-features {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          align-items: flex-start;
        }

        .cta-feature {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-weight: 500;
        }

        .cta-actions {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .btn-xl {
          padding: 1rem 2rem;
          font-size: 1.125rem;
          font-weight: 600;
        }

        .btn-ghost {
          background: transparent;
          color: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .btn-ghost:hover {
          background: rgba(255, 255, 255, 0.1);
          color: white;
        }

        @media (max-width: 768px) {
          .hero-title {
            font-size: 2.5rem;
          }

          .hero-actions {
            flex-direction: column;
          }

          .steps-grid {
            grid-template-columns: 1fr;
            gap: 3rem;
          }

          .readiness-card {
            padding: 2rem;
          }

          .cta-content {
            grid-template-columns: 1fr;
            text-align: center;
            padding: 2rem;
          }

          .cta-features {
            align-items: center;
          }

          .footer-content {
            grid-template-columns: 1fr;
          }

          .nav-actions {
            gap: 0.5rem;
          }
        }
      `}</style>
    </div>
  )
}

export default LandingScreen